"use client"

import type React from "react"
import { useEffect, useState, useMemo, useCallback } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { ChevronRight, Star, Truck, Shield, Award, RefreshCw, ChevronLeft } from "lucide-react"
import type { Product } from "../store/slices/productsSlice"
import { setProducts } from "../store/slices/productsSlice"
import ProductCard from "../components/common/ProductCard"
import LazyImage from "../components/common/LazyImage"
import SEOHead from "../components/common/SEOHead"
import { productService } from "../services/productService"
import { bannerService, type Banner } from "../services/bannerService"
import LoadingSpinner from "../components/common/LoadingSpinner"

const Home: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([])
  const [newArrivals, setNewArrivals] = useState<Product[]>([])
  const [bestSellers, setBestSellers] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Carousel state
  const [banners, setBanners] = useState<Banner[]>([])
  const [bannersLoading, setBannersLoading] = useState(true)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)
  const [isTransitioning, setIsTransitioning] = useState(false)

  // Load banners from Firebase - NO HARDCODED FALLBACKS
  useEffect(() => {
    const loadBanners = async () => {
      try {
        setBannersLoading(true)
        const activeBanners = await bannerService.getActiveBanners()
        setBanners(activeBanners) // Use only Firebase banners
        setCurrentSlide(0) // Reset to first slide when banners change
      } catch (error) {
        console.error("Error loading banners:", error)
        setBanners([]) // Empty array if Firebase fails
      } finally {
        setBannersLoading(false)
      }
    }

    loadBanners()

    // Listen for banner updates from admin panel
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "bannerUpdate") {
        loadBanners()
      }
    }

    // Listen for custom events from admin panel
    const handleBannerUpdate = () => {
      loadBanners()
    }

    window.addEventListener("storage", handleStorageChange)
    window.addEventListener("bannerUpdate", handleBannerUpdate)

    return () => {
      window.removeEventListener("storage", handleStorageChange)
      window.removeEventListener("bannerUpdate", handleBannerUpdate)
    }
  }, [])

  // Auto-slide functionality - ONLY for multiple banners
  useEffect(() => {
    if (!isPlaying || banners.length <= 1) return

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % banners.length)
    }, 5000)

    return () => clearInterval(interval)
  }, [isPlaying, banners.length])

  // Navigation functions
  const goToSlide = useCallback(
    (index: number) => {
      if (isTransitioning) return
      setIsTransitioning(true)
      setCurrentSlide(index)
      setTimeout(() => setIsTransitioning(false), 300)
    },
    [isTransitioning],
  )

  const nextSlide = useCallback(() => {
    if (isTransitioning) return
    goToSlide((currentSlide + 1) % banners.length)
  }, [currentSlide, banners.length, goToSlide, isTransitioning])

  const prevSlide = useCallback(() => {
    if (isTransitioning) return
    goToSlide((currentSlide - 1 + banners.length) % banners.length)
  }, [currentSlide, banners.length, goToSlide, isTransitioning])

  const togglePlayPause = useCallback(() => {
    setIsPlaying(!isPlaying)
  }, [isPlaying])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault()
        prevSlide()
      } else if (event.key === "ArrowRight") {
        event.preventDefault()
        nextSlide()
      } else if (event.key === " ") {
        event.preventDefault()
        togglePlayPause()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [nextSlide, prevSlide, togglePlayPause])

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true)
      setError(null)
      try {
        const [featured, newArr, best] = await Promise.all([
          productService.getFeaturedProducts(8),
          productService.getNewArrivals(8),
          productService.getBestSellers(8),
        ])

        // Enhanced mapping to Redux Product type with better safety and FIXED categories handling
        const mapToReduxProduct = (p: unknown): Product => {
          const obj = p as Record<string, unknown>

          // FIXED: Ensure categories is always an array and properly handle category field
          let categories: string[] = []
          if (Array.isArray(obj.categories)) {
            categories = obj.categories.filter(Boolean)
          } else if (obj.category) {
            categories = [obj.category as string]
          } else {
            categories = ["furniture"] // default fallback
          }

          return {
            id: (obj.id as string) || Math.random().toString(36).substr(2, 9),
            name: (obj.name as string) || "Unnamed Product",
            price: (obj.price as number) || 0,
            originalPrice: (obj.originalPrice as number) ?? (obj.price as number),
            images: Array.isArray(obj.images)
              ? obj.images.filter(Boolean)
              : obj.image
                ? [obj.image as string].filter(Boolean)
                : [],
            image: (obj.images as string[])?.[0] || (obj.image as string) || "", // Ensure image field
            description: (obj.description as string) || "",
            features: Array.isArray(obj.features) ? obj.features : [],
            categories: categories, // FIXED: Always provide categories array
            category: categories[0] || "furniture", // FIXED: Primary category from categories
            subcategory: (obj.subcategory as string) || "",
            rating: (obj.averageRating as number) || (obj.rating as number) || 0,
            reviews: (obj.totalRatings as number) || (obj.reviews as number) || 0,
            stock: Math.max(0, (obj.stock as number) || 1),
            tags: Array.isArray(obj.tags) ? obj.tags : [],
            dimensions: (obj.dimensions as { length: number; width: number; height: number }) ?? undefined,
            material: (obj.material as string) || "",
            warranty: (obj.warranty as string) || "",
            isFeatured: Boolean(obj.isFeatured),
            isNewArrival: Boolean(obj.isNewArrival),
            isBestSeller: Boolean(obj.isBestSeller),
            createdAt: obj.createdAt
              ? typeof (obj.createdAt as { toDate?: () => Date }).toDate === "function"
                ? (obj.createdAt as { toDate: () => Date }).toDate().toISOString()
                : (obj.createdAt as string)
              : new Date().toISOString(),
          }
        }

        const featuredMapped = featured.map(mapToReduxProduct).filter((p) => p.id && p.name)
        const newArrivalsMapped = newArr.map(mapToReduxProduct).filter((p) => p.id && p.name)
        const bestSellersMapped = best.map(mapToReduxProduct).filter((p) => p.id && p.name)

        setFeaturedProducts(featuredMapped)
        setNewArrivals(newArrivalsMapped)
        setBestSellers(bestSellersMapped)

        // Merge all products by id
        const allProductsMap = new Map<string, Product>()
        for (const p of [...featuredMapped, ...newArrivalsMapped, ...bestSellersMapped]) {
          if (p && p.id && p.name) {
            allProductsMap.set(p.id, p)
          }
        }
        dispatch(setProducts(Array.from(allProductsMap.values())))
      } catch (err) {
        console.error("Error fetching products:", err)
        setError("Failed to load products. Please try again later.")
      } finally {
        setLoading(false)
      }
    }
    fetchProducts()
  }, [dispatch])

  const categories = [
    {
      name: "Beds",
      slug: "beds",
      image: "https://images.pexels.com/photos/164595/pexels-photo-164595.jpeg?auto=compress&cs=tinysrgb&w=400",
      description: "Comfortable & Stylish",
    },
    {
      name: "Sofas",
      slug: "sofas",
      image: "https://images.pexels.com/photos/1148955/pexels-photo-1148955.jpeg?auto=compress&cs=tinysrgb&w=400",
      description: "Luxurious Seating",
    },
    {
      name: "Dining",
      slug: "dining",
      image: "https://images.pexels.com/photos/1080696/pexels-photo-1080696.jpeg?auto=compress&cs=tinysrgb&w=400",
      description: "Family Gatherings",
    },
    {
      name: "Wardrobes",
      slug: "wardrobes",
      image: "https://images.pexels.com/photos/1957477/pexels-photo-1957477.jpeg?auto=compress&cs=tinysrgb&w=400",
      description: "Organized Storage",
    },
    {
      name: "Chairs",
      slug: "chairs",
      image: "https://images.pexels.com/photos/4219654/pexels-photo-4219654.jpeg?auto=compress&cs=tinysrgb&w=400",
      description: "Ergonomic Design",
    },
    {
      name: "Tables",
      slug: "tables",
      image: "https://images.pexels.com/photos/1866149/pexels-photo-1866149.jpeg?auto=compress&cs=tinysrgb&w=400",
      description: "Functional Beauty",
    },
  ]

  const features = [
    {
      icon: Truck,
      title: "Free Delivery",
      description: "Free delivery on orders above ₹25,000",
    },
    {
      icon: Shield,
      title: "Warranty",
      description: "Up to 3 years warranty on select items",
    },
    {
      icon: Award,
      title: "Quality Assured",
      description: "Premium quality furniture from trusted brands",
    },
    {
      icon: RefreshCw,
      title: "Easy Returns",
      description: "30-day hassle-free return policy",
    },
  ]

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Anmol Furniture Bazaar - Premium Home Furniture Store",
    description:
      "Transform your home with premium furniture from Anmol Furniture Bazaar. Shop beds, sofas, dining sets, wardrobes & more with free delivery and warranty.",
    url: "https://anmolfurniture.com",
    mainEntity: {
      "@type": "Store",
      name: "Anmol Furniture Bazaar",
      image: "https://anmolfurniture.com/logo.png",
      priceRange: "₹₹₹",
      telephone: "+91-98765-43210",
    },
  }

  // Memoize product lists for rendering
  const memoizedFeaturedProducts = useMemo(() => featuredProducts, [featuredProducts])
  const memoizedNewArrivals = useMemo(() => newArrivals, [newArrivals])
  const memoizedBestSellers = useMemo(() => bestSellers, [bestSellers])

  // Calculate actual average rating and total ratings from all products
  const allProducts = useMemo(
    () => [...featuredProducts, ...newArrivals, ...bestSellers],
    [featuredProducts, newArrivals, bestSellers],
  )

  const ratingStats = useMemo(() => {
    const validProducts = allProducts.filter((p) => p && p.rating !== undefined && p.reviews !== undefined)
    const ratings = validProducts.map((p) => p.rating || 0)
    const numRatings = validProducts.map((p) => p.reviews || 0)
    const totalRatings = numRatings.reduce((a, b) => a + b, 0)
    const avgRating = ratings.length > 0 ? ratings.reduce((a, b) => a + b, 0) / ratings.length : 4.6
    return { avgRating: avgRating.toFixed(1), totalRatings: Math.max(totalRatings, 200) }
  }, [allProducts])

  // FIXED: Only show categories that have at least one product - check categories array
  const categoriesWithProducts = categories.filter((cat) =>
    allProducts.some((p) => {
      const productCategories = p.categories || [p.category]
      return productCategories.some((pCat) => pCat && pCat.toLowerCase().includes(cat.slug.toLowerCase()))
    }),
  )

  const currentBanner = banners[currentSlide] || banners[0]

  // Helper to generate product detail URL (matches ProductCard logic)
  function slugify(str: string | undefined | null): string {
    if (!str) return ""
    return str
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
  }
  function getProductUrl(productId: string) {
    const product = allProducts.find((p) => p.id === productId)
    if (!product) return `/product/${productId}`
    const categorySlug = slugify(product.categories?.[0] || product.category || "furniture")
    const nameSlug = slugify(product.name) || productId
    return `/${categorySlug}/${nameSlug}`
  }

  return (
    <>
      <SEOHead
        title="Anmol Furniture Bazaar - Premium Home Furniture Store | Transform Your Home"
        description="Transform your home with premium furniture from Anmol Furniture Bazaar. Shop beds, sofas, dining sets, wardrobes & more. Free delivery, 3-year warranty, easy returns."
        keywords="premium furniture, home furniture, beds, sofas, dining table, wardrobe, furniture store, home decor, quality furniture"
        structuredData={structuredData}
      />

      <main className="min-h-screen">
        {/* Hero Carousel Section - Full Width & Responsive */}
        <section className="relative w-full overflow-hidden">
  {bannersLoading ? (
    <div className="flex items-center justify-center h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] xl:h-[700px] bg-gray-100">
      <LoadingSpinner size="lg" />
    </div>
  ) : banners.length === 0 ? (
    <div className="flex items-center justify-center h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] xl:h-[700px] bg-gray-100">
      <div className="text-center px-4">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">No Banners Available</h2>
        <p className="text-gray-600">Add banners from the admin panel to display content here.</p>
      </div>
    </div>
  ) : banners.length === 1 ? (
    // Single banner - Static display
    <div className="relative w-full h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] xl:h-[700px]">
      {banners[0].type === "image-only" ? (
        // Full-width image-only banner
        <button
          type="button"
          className="relative w-full h-full block group cursor-pointer p-0 m-0 border-0 bg-transparent"
          style={{ all: "unset", display: "block", width: "100%", height: "100%", cursor: "pointer" }}
          onClick={() => {
            if (banners[0].linkedProductId) {
              navigate(getProductUrl(banners[0].linkedProductId))
            } else if (banners[0].ctaLink) {
              navigate(banners[0].ctaLink)
            } else {
              navigate("/categories")
            }
          }}
          aria-label="Go to banner link"
        >
          <LazyImage
            src={banners[0].image}
            alt="Hero banner"
            className="w-full h-full object-cover object-center"
            loading="eager"
            width={1920}
            height={700}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
        </button>
      ) : (
        // Text with image banner - Mobile: no image, Desktop: with image
        <div className="relative w-full h-full bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-2xl overflow-hidden shadow-2xl">
          {/* Animated Background Elements */}
          <div className="absolute inset-0">
            <div className="absolute top-10 left-8 w-20 h-20 bg-white/10 rounded-full blur-xl animate-pulse"></div>
            <div className="absolute bottom-16 right-6 w-16 h-16 bg-yellow-300/20 rounded-full blur-lg animate-bounce"></div>
            <div className="absolute top-1/2 left-4 w-12 h-12 bg-pink-300/15 rounded-full blur-md animate-pulse delay-700"></div>
            <div className="absolute bottom-8 left-1/3 w-8 h-8 bg-blue-300/20 rounded-full blur-sm animate-bounce delay-1000"></div>
          </div>

          {/* Main Content Container */}
          <div className="relative z-10 h-full flex flex-col justify-center items-center text-center p-6 sm:p-8">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs sm:text-sm font-medium mb-4 border border-white/30">
              <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
              Limited Time Offer
            </div>

            {/* Main Title */}
            <h1 className="text-3xl sm:text-4xl font-black text-white leading-tight mb-3 tracking-tight">
              {banners[0].title}
            </h1>

            {/* Subtitle with Emphasis */}
            <div className="text-xl sm:text-2xl font-bold text-yellow-300 mb-4 drop-shadow-lg">
              {banners[0].subtitle}
            </div>

            {/* Description */}
            {banners[0].description && (
              <p className="text-base sm:text-lg text-white/90 leading-relaxed max-w-sm mx-auto mb-6 font-medium">
                {banners[0].description}
              </p>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-col w-full max-w-xs space-y-3">
              <Link
                to={banners[0].ctaLink || "/categories"}
                className="group relative bg-white text-gray-900 px-8 py-4 rounded-2xl font-bold text-base shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-orange-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <span className="relative z-10 flex items-center justify-center">
                  {banners[0].ctaText || "Shop Now"}
                  <span className="ml-2 text-xl group-hover:translate-x-1 transition-transform duration-300">
                    →
                  </span>
                </span>
              </Link>

              <Link
                to="/categories"
                className="bg-white/10 backdrop-blur-sm border-2 border-white/40 text-white px-8 py-4 rounded-2xl font-semibold text-base hover:bg-white/20 hover:border-white/60 transition-all duration-300 shadow-lg"
              >
                Browse All Categories
              </Link>
            </div>
          </div>

          {/* Floating Rating Badge */}
          <div className="absolute top-6 right-6 bg-white/95 backdrop-blur-sm px-4 py-3 rounded-2xl shadow-xl">
            <div className="flex items-center space-x-2">
              <Star className="text-yellow-400 fill-yellow-400 w-5 h-5" />
              <div className="text-right">
                <div className="font-black text-gray-900 text-lg leading-none">{ratingStats.avgRating}</div>
                <div className="text-xs text-gray-600 font-medium">{ratingStats.totalRatings}+ reviews</div>
              </div>
            </div>
          </div>

          {/* Bottom Accent Line */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-pink-400 to-blue-400"></div>
        </div>
      )}
    </div>
  ) : (
    // Multiple banners - Carousel
    <div className="relative group">
      <div className="relative h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] xl:h-[700px] overflow-hidden">
        {banners.map((banner, index) => (
          <div
            key={banner.id}
            className={`absolute inset-0 transition-all duration-700 ease-in-out transform ${
              index === currentSlide
                ? "translate-x-0 opacity-100 z-10"
                : index < currentSlide
                  ? "-translate-x-full opacity-0 z-0"
                  : "translate-x-full opacity-0 z-0"
            }`}
          >
            {banner.type === "image-only" ? (
              <button
                type="button"
                className="relative w-full h-full block group cursor-pointer p-0 m-0 border-0 bg-transparent"
                style={{ all: "unset", display: "block", width: "100%", height: "100%", cursor: "pointer" }}
                onClick={() => {
                  if (banner.linkedProductId) {
                    navigate(getProductUrl(banner.linkedProductId))
                  } else if (banner.ctaLink) {
                    navigate(banner.ctaLink)
                  } else {
                    navigate("/categories")
                  }
                }}
                aria-label="Go to banner link"
              >
                <LazyImage
                  src={banner.image}
                  alt="Hero banner"
                  className="w-full h-full object-cover object-center"
                  loading={index === 0 ? "eager" : "lazy"}
                  width={1920}
                  height={700}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
              </button>
            ) : (
              // Text with image banner - Mobile: single column, Desktop: two columns
              <div className="relative w-full h-full bg-gradient-to-br from-primary-50 to-primary-100">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-full">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center h-full py-8 sm:py-12 lg:py-16">
                    {/* Text Content */}
                    <div className="space-y-3 sm:space-y-4 lg:space-y-6 text-center lg:text-left z-20 relative order-1">
                      <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-primary-900 leading-tight">
                        {banner.title}
                        <span className="text-primary-600 block text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl">
                          {banner.subtitle}
                        </span>
                      </h1>
                      <p className="text-sm sm:text-base lg:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                        {banner.description}
                      </p>
                      <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 justify-center lg:justify-start pt-2">
                        <Link
                          to={banner.ctaLink || "/categories"}
                          className="bg-primary-600 text-white px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm sm:text-base"
                        >
                          {banner.ctaText || "Shop Now"}
                        </Link>
                        <Link
                          to="/categories"
                          className="border-2 border-primary-600 text-primary-600 px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 rounded-lg hover:bg-primary-600 hover:text-white transition-colors font-medium text-sm sm:text-base"
                        >
                          Browse All Categories
                        </Link>
                      </div>
                    </div>
                    
                    {/* Image - Hidden on mobile (< lg), shown on desktop */}
                    <div className="relative hidden lg:block order-2">
                      <LazyImage
                        src={banner.image}
                        alt={`${banner.title} - ${banner.subtitle}`}
                        className="rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl w-full h-[200px] sm:h-[250px] md:h-[300px] lg:h-[350px] xl:h-[450px] object-cover object-center"
                        loading={index === 0 ? "eager" : "lazy"}
                        width={800}
                        height={450}
                      />
                      <div className="absolute -bottom-2 -left-2 sm:-bottom-4 sm:-left-4 lg:-bottom-6 lg:-left-6 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-xl shadow-lg">
                        <div className="flex items-center space-x-1">
                          <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                          <span className="text-xs font-medium text-gray-900">Limited Time</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Navigation Arrows - Only show for multiple banners */}
      <div className="absolute inset-0 flex justify-between items-center pointer-events-none px-2 sm:px-4 lg:px-8">
        <button
          onClick={prevSlide}
          disabled={isTransitioning}
          className="pointer-events-auto opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 hover:bg-white text-gray-800 p-2 sm:p-3 rounded-full shadow-lg hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed z-20"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
        </button>
        <button
          onClick={nextSlide}
          disabled={isTransitioning}
          className="pointer-events-auto opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 hover:bg-white text-gray-800 p-2 sm:p-3 rounded-full shadow-lg hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed z-20"
          aria-label="Next slide"
        >
          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
        </button>
      </div>

      {/* Pagination Dots */}
      <div className="absolute bottom-3 sm:bottom-4 lg:bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center space-x-2 sm:space-x-3">
        <div className="flex space-x-1 sm:space-x-2">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              disabled={isTransitioning}
              className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
                index === currentSlide ? "bg-white w-4 sm:w-6 lg:w-8" : "bg-white/60 hover:bg-white/80"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Slide Counter */}
      <div className="absolute top-3 sm:top-4 right-3 sm:right-4 z-30 bg-black/30 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
        {currentSlide + 1} / {banners.length}
      </div>
    </div>
  )}
</section>

{/* Features */}
<section className="py-12 sm:py-16 bg-white" aria-label="Our Features">
  <div className="container mx-auto px-4">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
      {features.map((feature, index) => (
        <div key={index} className="text-center group">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:bg-primary-200 transition-colors">
            <feature.icon className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">{feature.title}</h3>
          <p className="text-xs sm:text-sm text-gray-600">{feature.description}</p>
        </div>
      ))}
    </div>
  </div>
</section>

        {/* Categories Section */}
        {categoriesWithProducts.length > 0 && (
          <section className="py-12 sm:py-16 bg-gray-50" aria-label="Product Categories">
            <div className="container mx-auto px-4">
              <div className="text-center mb-8 sm:mb-12">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Shop by Categories</h2>
                <p className="text-gray-600 text-sm sm:text-base">Find the perfect furniture for every rooms!</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6">
                {categoriesWithProducts.map((category, index) => (
                  <Link key={index} to={`/categories/${category.slug}`} className="group text-center">
                    <div className="aspect-square rounded-2xl overflow-hidden mb-3 sm:mb-4 bg-gray-200">
                      <LazyImage
                        src={category.image}
                        alt={`${category.name} furniture category`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        width={200}
                        height={200}
                      />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">{category.name}</h3>
                    <p className="text-xs sm:text-sm text-gray-600">{category.description}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Loading state for products */}
        {loading && (
          <div className="py-12 sm:py-16 bg-white">
            <div className="container mx-auto px-4">
              <div className="flex justify-center items-center min-h-[200px]">
                <LoadingSpinner />
              </div>
            </div>
          </div>
        )}

        {/* Error state for products */}
        {error && (
          <div className="py-12 sm:py-16 bg-white">
            <div className="container mx-auto px-4">
              <div className="text-center text-red-500 py-8">
                <p className="text-lg font-medium mb-2">Oops! Something went wrong</p>
                <p className="text-sm">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-4 bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Featured Products */}
        {!loading && !error && memoizedFeaturedProducts.length > 0 && (
          <section className="py-12 sm:py-16 bg-white" aria-label="Featured Products">
            <div className="container mx-auto px-4">
              <div className="flex items-center justify-between mb-8 sm:mb-12">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Featured Products</h2>
                  <p className="text-gray-600 text-sm sm:text-base">Handpicked premium furniture for your home</p>
                </div>
                <Link
                  to="/products?featured=true"
                  className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-medium text-sm sm:text-base"
                >
                  <span>View All</span>
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {memoizedFeaturedProducts.slice(0, 4).map((product, index) => (
                  <ProductCard key={`featured-${product.id}`} product={product} priority={index < 2} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* New Arrivals */}
        {!loading && !error && memoizedNewArrivals.length > 0 && (
          <section className="py-12 sm:py-16 bg-gray-50" aria-label="New Arrivals">
            <div className="container mx-auto px-4">
              <div className="flex items-center justify-between mb-8 sm:mb-12">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">New Arrivals</h2>
                  <p className="text-gray-600 text-sm sm:text-base">Latest additions to our furniture collection</p>
                </div>
                <Link
                  to="/products?new=true"
                  className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-medium text-sm sm:text-base"
                >
                  <span>View All</span>
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {memoizedNewArrivals.slice(0, 4).map((product) => (
                  <ProductCard key={`new-${product.id}`} product={product} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Best Sellers */}
        {!loading && !error && memoizedBestSellers.length > 0 && (
          <section className="py-12 sm:py-16 bg-white" aria-label="Best Sellers">
            <div className="container mx-auto px-4">
              <div className="flex items-center justify-between mb-8 sm:mb-12">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Best Sellers</h2>
                  <p className="text-gray-600 text-sm sm:text-base">Most popular furniture loved by our customers</p>
                </div>
                <Link
                  to="/products?bestseller=true"
                  className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-medium text-sm sm:text-base"
                >
                  <span>View All</span>
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {memoizedBestSellers.slice(0, 4).map((product) => (
                  <ProductCard key={`bestseller-${product.id}`} product={product} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* No Products State */}
        {!loading &&
          !error &&
          memoizedFeaturedProducts.length === 0 &&
          memoizedNewArrivals.length === 0 &&
          memoizedBestSellers.length === 0 && (
            <section className="py-12 sm:py-16 bg-white">
              <div className="container mx-auto px-4">
                <div className="text-center py-12">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">No Products Available</h2>
                  <p className="text-gray-600 mb-6">We're working on adding amazing furniture to our collection.</p>
                  <Link
                    to="/categories"
                    className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium"
                  >
                    Explore Categories
                  </Link>
                </div>
              </div>
            </section>
          )}
      </main>
    </>
  )
}

export default Home
